import React, {useRef, useState, useEffect, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {Store} from 'src/types/store';
import {withCommas} from 'shared/utils';
import {
  removeAwardNotificationFromQueue,
  setActiveAwardNotification,
  unsetActiveAwardNotification,
} from 'shared/actions/award-notifications';

import Icon from 'shared/components/icon';
import {AWARD_MAP} from './award-map';

const AwardNotification = () => {
  const dispatch = useDispatch();
  const descriptionRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const [showLocation, setShowLocation] = useState(false);
  const {activeAward} = useSelector((state: Store) => state.awardNotifications);
  const mappedAward = AWARD_MAP[activeAward?.scholarship._type];

  const cleanUpNotification = useCallback(() => {
    dispatch(removeAwardNotificationFromQueue());
    dispatch(unsetActiveAwardNotification());
    setTimeout(() => {
      dispatch(setActiveAwardNotification());
    }, 1000);
  }, [dispatch]);

  const scheduleTimeout = useCallback(() => {
    timeoutRef.current = setTimeout(() => cleanUpNotification(), 5000);
  }, [cleanUpNotification]);

  useEffect(() => {
    scheduleTimeout();

    if (descriptionRef.current) {
      setShowLocation(descriptionRef.current.offsetHeight / 24 < 2);
    }
  }, [scheduleTimeout]);

  if (!activeAward) return null;

  const {college, amount} = activeAward;

  return (
    <div
      role="alert"
      aria-live="polite"
      className={`shared-award-notification ${mappedAward.type}`}
      onMouseOver={() => clearTimeout(timeoutRef.current)}
      onMouseOut={scheduleTimeout}
    >
      <Icon iconType={mappedAward.icon} className="shared-award-notification-icon" />
      <div className="shared-award-notification-content">
        <div className="shared-award-notification-shout-out">{mappedAward.salutation}</div>
        <div
          className="shared-award-notification-description"
          ref={descriptionRef}
          dangerouslySetInnerHTML={{__html: mappedAward.description(activeAward)}}
        ></div>
        <div className="shared-award-notification-college-info">
          <div className="shared-award-notification-college-name">
            {college.nickname || college.name}
          </div>
          {showLocation && `${college.city}, ${college.state}`}
        </div>
        <div className="shared-award-notification-amount">${withCommas(Math.floor(amount))}</div>
      </div>
      <Icon
        iconType="times-r"
        className="shared-award-notification-close"
        onClick={cleanUpNotification}
      />
    </div>
  );
};

export default AwardNotification;
